import * as fflate from 'fflate';
import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { ContourEditBlocker } from '~/domain/dicom/contourEditBlocker';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { EventSubscriptions } from '~/domain/events/EventSubscriptions';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { DicomContour } from '../../../domain/dicom/dicomstructureset';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
    RoiContoursModifiedEvent,
    RoiPropertiesUpdatedEvent,
} from '../../../domain/events';
import { ViewManager } from '../../view/ViewManager';
import { Grid } from '../common/Grid';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { Contour } from './Contour';
import { ContourSequence } from './ContourSequence';
import { ContourSlice } from './ContourSlice';
import { ContourTexture } from './ContourTexture';
import { RoiStatistics } from '~/engine/util/algorithms/sdf/ContourStatistics';
import { maskIntensityStatistics } from '~/engine/util/algorithms/sdf/MaskIntensityStatistics';
import { contoursCoverTexelCentre } from '~/engine/util/algorithms/sdf/stencilCoverage';
import { Image3D, imageIntensityPixels } from '~/domain/patient/image3d';
import { SummaryStatistics } from '~/domain/statistics/summaryStatistics';
import { dicomColorToVec3 } from '~/utils/color';

/** Options for {@link RegionOfInterest.startModify}. */
export interface StartModifyOptions {
    /**
     * The write produces the structure's ENTIRE content rather than
     * editing part of it — a boolean result, a margin, a ring, a union, a
     * threshold extraction. Only such a write may land on a structure
     * that has no contours yet and leave no prior state behind it; an
     * incremental edit always records one, so its first application can
     * be undone back to the empty structure.
     */
    authorsWholeStructure?: boolean;
    /**
     * The caller puts the texture back as it was when
     * {@link TextureWrite.finish} throws, so a finish that fails leaves the
     * structure unmarked. Without it the texture keeps the write, and a
     * finish that fails still marks the structure changed.
     */
    restoredOnFailure?: boolean;
}

/**
 * An ROI's image-intensity statistics with the image they came from. The
 * statistics are unit-less, so the display side needs the header to label them
 * HU / SUV / bare. One object, so the reactive read returns one stable
 * reference.
 */
export interface RoiIntensityStats {
    /** Min/max/mean/SD of the image voxels under the mask, in native units. */
    readonly statistics: SummaryStatistics;
    /** The image the statistics were sampled from (for unit labelling). */
    readonly image: Image3D;
}

/** Options for {@link TextureWrite.stageTexture}. */
export interface StagedTextureOptions {
    /** Edit-history label, joined to the write's own label. */
    operation?: string;
    /** Called after the write has put the replaced texture back. */
    onReverted?: () => void;
}

interface StagedTexture extends StagedTextureOptions {
    replaced: ContourTexture | undefined;
}

const reinstateTexture = Symbol('reinstateTexture');

/**
 * Helper class to manage the region of interest texture modifications.
 */
export class TextureWrite {
    private readonly initialPixels?: Uint8Array[];
    private readonly initialGrid?: Grid;
    private staged?: StagedTexture;
    private stagingSettled = false;

    constructor(
        public readonly roi: RegionOfInterest,
        public readonly operation: string,
        initialPixels?: Uint8Array[],
        initialGrid?: Grid,
        private readonly restoredOnFailure = false
    ) {
        this.initialPixels = initialPixels;
        this.initialGrid = initialGrid;
    }

    public get texture(): ContourTexture | undefined {
        return this.roi.texture;
    }

    /**
     * Makes `texture` the structure's texture for the rest of this write. It
     * follows startModify, so the first-edit snapshot never holds it. finish()
     * deletes the replaced texture; cancel() or a failed finish() restores it.
     */
    public stageTexture(
        texture: ContourTexture,
        options: StagedTextureOptions = {}
    ): void {
        if (this.staged || this.stagingSettled) {
            throw new Error('TextureWrite: a write stages at most one texture');
        }
        const replaced = this.roi.builtTexture;
        if (texture === replaced) {
            throw new Error(
                'TextureWrite: the structure already holds the texture to stage'
            );
        }
        this.roi.texture = texture;
        this.staged = { ...options, replaced };
    }

    public get hasStagedTexture(): boolean {
        return this.staged !== undefined;
    }

    public cancel(): void {
        try {
            this.revertStaging();
        } finally {
            this.roi.resetModificationFlag();
        }
    }

    private takeStaging(): StagedTexture | undefined {
        const staged = this.staged;
        if (staged) {
            this.staged = undefined;
            this.stagingSettled = true;
        }
        return staged;
    }

    private revertStaging(): void {
        const staged = this.takeStaging();
        if (!staged) {
            return;
        }
        this.roi[reinstateTexture](staged.replaced);
        staged.onReverted?.();
    }

    /**
     * A copy of the texture's slices. When copying throws, the texture
     * already holds the write: unless the caller restores it, the ROI is
     * marked changed so a save carries what the viewer shows.
     * A staged texture is reverted instead.
     */
    private copyPixels(texture?: ContourTexture): Uint8Array[] | undefined {
        try {
            return texture?.pixels?.map((slice) => slice.slice());
        } catch (err) {
            if (this.staged) {
                this.revertStaging();
            } else if (!this.restoredOnFailure) {
                this.roi.markAsChanged();
            }
            throw err;
        }
    }

    public finish() {
        const texture = this.roi.texture;
        const pixels = this.copyPixels(texture);
        const staged = this.takeStaging();
        this.roi.markAsChanged();

        if (texture) {
            const grid = texture.textureGrid;
            // Identity comes from the ROI's owning structure set, never from
            // whichever structure set is currently selected.
            const structureSetUid = this.roi.structureSetUid;
            const roiNumber = this.roi.roiNumber;
            const operation = staged?.operation
                ? `${this.operation} + ${staged.operation}`
                : this.operation;
            const initialPixels = this.initialPixels;
            const initialGrid = this.initialGrid;
            if (pixels) {
                setTimeout(() => {
                    const event = new RoiContoursModifiedEvent(
                        structureSetUid,
                        roiNumber,
                        operation,
                        pixels,
                        grid,
                        initialPixels,
                        initialGrid
                    );
                    domainEventBus.publish(event).catch((error) => {
                        console.error(
                            `Error publishing contour event: ${error}`
                        );
                    });
                }, 0);
            }
        }
        staged?.replaced?.delete();
    }
}

export class RegionOfInterest {
    public readonly roiNumber: number;

    private _roiName: string;
    private _color8: DicomColor = { R: 255, G: 0, B: 0 };
    private _color: ReadonlyVec3 = vec3.fromValues(1, 0, 0);
    private _displayColorOverride: ReadonlyVec3 | null = null;
    private _contourThickness = 1;
    private _visible = true;

    private readonly contourSequence: ContourSequence;

    private _allSlicesChanged = false;
    private _modificationStarted = false;

    // Indicates if the ROI has been touched/modified
    private _touched = false;

    private _deleted = false;

    private contourTexture?: ContourTexture;

    /**
     * Whether the source contours would rasterise to any texel; answers
     * {@link isEmpty} while no texture exists. Cleared whenever the
     * texture or the sequence changes.
     */
    private _sourceContoursCoverTexel?: boolean;

    private _eventSubscriptions?: EventSubscriptions;

    // Segment version tracking (increments whenever contour pixels / texture SDF change)
    private _sdfVersion = 0;
    private _sdfModifiedAt = Date.now(); // epoch ms timestamp of last contour change

    public constructor(
        private parentRoi: DicomRoi,
        public readonly _imageGrid: Grid,
        public readonly structureSetUid: string
    ) {
        this.roiNumber = parentRoi.roiNumber;
        this._roiName = parentRoi.name;
        this._visible = parentRoi.visible;
        this._contourThickness = parentRoi.contourThickness;

        this.colorRgb8 = parentRoi.color;

        const slices = RegionOfInterest.toSlices(
            parentRoi.contours,
            this._imageGrid
        );
        this.contourSequence = new ContourSequence(this._imageGrid, slices);

        const subscriptions = [
            domainEventBus.subscribe(
                RoiPropertiesUpdatedEvent.EVENT_TYPE,
                this.handleRoiPropertiesUpdated.bind(this)
            ),
            domainEventBus.subscribe(
                ContourUndoRequestedEvent.EVENT_TYPE,
                this.handleContourUndoRedoRequested.bind(this)
            ),
            domainEventBus.subscribe(
                ContourRedoRequestedEvent.EVENT_TYPE,
                this.handleContourUndoRedoRequested.bind(this)
            ),
        ];

        this._eventSubscriptions = new EventSubscriptions(subscriptions);
    }

    public get isVisible(): boolean {
        return this._visible;
    }

    public set visibility(visible: boolean) {
        if (this._visible !== visible) {
            this._visible = visible;
        }
    }

    public get contourThickness(): number {
        return this._contourThickness;
    }

    public set contourThickness(contourThickness: number) {
        this._contourThickness = contourThickness;
    }

    /**
     * Gates contour GEOMETRY only: approval and the edit lock both block a
     * shape change. Metadata edits stay allowed on a locked ROI.
     */
    public get canModifyContours(): boolean {
        return this.parentRoi.canModifyContours;
    }

    /**
     * Which guard blocks a contour edit, or undefined when none does;
     * approval outranks the lock (see resolveContourEditBlocker).
     */
    public get contourEditBlocker(): ContourEditBlocker | undefined {
        return this.parentRoi.contourEditBlocker;
    }

    /** Adjective for refusal messages, naming the blocking guard. */
    private get blockerAdjective(): 'locked' | 'approved' {
        return this.contourEditBlocker === 'lock' ? 'locked' : 'approved';
    }

    private _ensureCanModifyContours(operation: string): void {
        if (!this.canModifyContours) {
            throw new Error(
                `Cannot modify ${this.blockerAdjective} structure` +
                    ` '${this.name}' (operation: ${operation})`
            );
        }
    }

    /** Whether this ROI represents a single point (MARKER, ISOCENTER, REGISTRATION). */
    public get isPointRoi(): boolean {
        return this.parentRoi.isPointRoi;
    }

    /** The point coordinates in mm for point-type ROIs, or null. */
    public get pointCoordinatesMm(): ReadonlyVec3 | null {
        return this.parentRoi.pointCoordinatesMm;
    }

    /**
     * Method to get reference to {@link contourSequence}-data.\
     * This updates the {@link ContourSequence} before returning it, if the data is not up-to-date.
     */
    public get sequence(): ContourSequence {
        if (this.isPointRoi) {
            return this.contourSequence;
        }
        if (!this.isSequenceUpToDate) {
            if (this.contourTexture) {
                this.updateSequence();
            } else {
                throw new Error(
                    'RegionOfInterest get sequence(): texture is undefined'
                );
            }
        }
        return this.contourSequence;
    }

    public get hasBeenModified(): boolean {
        return this._allSlicesChanged;
    }

    public get isSequenceUpToDate(): boolean {
        return this._allSlicesChanged === false;
    }

    private checkModificationFlag() {
        if (this._modificationStarted) {
            console.warn(
                `${this.name} updateSequence(): modifications flag on`
            );
        }
    }

    private updateSequence(): void {
        this.checkModificationFlag();
        this.contourSequence.updateFromContourTexture(this.contourTexture!);
        this._allSlicesChanged = false;
        this._sourceContoursCoverTexel = undefined;
    }

    private _renderErrors?: string = '';
    /**
     * Method to get reference to {@link contourTexture}-data.\
     * This updates the {@link ContourTexture} before returning it, if the data is not up-to-date.
     * Returns undefined for point ROIs which have no SDF texture.
     */
    public get texture(): ContourTexture | undefined {
        if (this.isPointRoi) {
            return undefined;
        }
        if (!this.contourTexture) {
            const { texture, renderErrors } =
                ContourTexture.fromContourSequence(
                    this._imageGrid,
                    this.contourSequence
                );
            this.contourTexture = texture;
            this._renderErrors = renderErrors;
        }
        return this.contourTexture;
    }

    public set texture(texture: ContourTexture | undefined) {
        if (texture) {
            this._ensureCanModifyContours('set texture');
        }
        this.holdTexture(texture);
    }

    private holdTexture(texture: ContourTexture | undefined): void {
        this.contourTexture = texture;
        this._sourceContoursCoverTexel = undefined;
        // Keyed on the field's data version, which starts again with
        // every texture.
        this._intensityStatsCached = undefined;
        if (texture) {
            this.bumpSdfVersion();
        }
    }

    /**
     * Puts `texture` back and deletes the one held instead. Module-private:
     * it skips the editability guard, because a restore is not an edit. On a
     * deleted ROI `texture` is deleted instead.
     */
    public [reinstateTexture](texture: ContourTexture | undefined): void {
        const held = this.contourTexture;
        if (held === texture) {
            return;
        }
        if (this._deleted) {
            texture?.delete();
            return;
        }
        held?.delete();
        this.holdTexture(texture);
    }

    /** The ROI's texture for drawing; undefined while it is hidden or has no contour. */
    public get drawableTexture(): ContourTexture | undefined {
        if (!this.isVisible) {
            return undefined;
        }
        const texture = this.texture;
        return texture && !this.isEmpty ? texture : undefined;
    }

    /**
     * The SDF texture if one exists, without building it. Undefined for a
     * point ROI and for a structure whose texture has not been built yet
     * — by a draw, an edit, or a content-derived quantity.
     */
    public get builtTexture(): ContourTexture | undefined {
        return this.contourTexture;
    }

    /**
     * Asserts that this ROI has an SDF texture.
     * Point ROIs (MARKER, ISOCENTER, REGISTRATION) have no
     * texture.
     */
    public assertHasTexture(): void {
        if (!this.texture) {
            throw new Error(`ROI '${this.name}' has no SDF texture`);
        }
    }

    public get renderErrors(): string | undefined {
        return this._renderErrors;
    }

    /**
     * The colour the structure is drawn in: the preview colour while one is
     * set (see {@link setDisplayColorOverride}), otherwise its own.
     */
    public get color(): ReadonlyVec3 {
        return this._displayColorOverride ?? this._color;
    }

    /** The structure's own 8-bit colour; never the preview colour. */
    public get colorRgb8(): DicomColor {
        return this._color8;
    }

    /**
     * Draws the structure in `color` instead of its own until cleared with
     * `null`. Display only: the structure's colour, {@link colorRgb8}, is
     * untouched. Redraws only the structure's own extent, and never builds
     * a texture to find it — an unbuilt structure draws nothing to redraw.
     */
    public setDisplayColorOverride(color: DicomColor | null): void {
        const next = color && dicomColorToVec3(color);
        const current = this._displayColorOverride;
        if (
            next === current ||
            (next && current && vec3.equals(next, current))
        ) {
            return;
        }
        this._displayColorOverride = next;
        const extent = this.builtTexture?.fullBoundingBox;
        if (extent) {
            ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT, extent);
        }
    }

    public set colorRgb8(color: DicomColor) {
        this._color8 = color;
        this._color = dicomColorToVec3(color);
    }

    /**
     * Whether the structure holds no contour content.
     *
     * Two sources, in this order: the SDF texture when it exists, since
     * edits land there; otherwise the load-time contour sequence, read
     * from the field rather than through {@link sequence}, which requires
     * a texture. The texture-less answer is what rasterising those
     * contours would give — empty unless some texel centre lies inside
     * them, the rule the stencil pass classifies texels by — so it holds
     * once the texture is built, and a zero-area or sub-voxel contour is
     * empty either way. Querying emptiness never builds the texture; the
     * content-derived quantities ({@link volumeCm3}, {@link statistics},
     * {@link boundingBox}) do.
     */
    public get isEmpty(): boolean {
        if (this.isPointRoi) {
            return this.pointCoordinatesMm === null;
        }
        if (this.contourTexture) {
            return this.contourTexture.isEmpty;
        }
        if (this._sourceContoursCoverTexel === undefined) {
            this._sourceContoursCoverTexel = contoursCoverTexelCentre(
                this._imageGrid,
                this.contourSequence
            );
        }
        return !this._sourceContoursCoverTexel;
    }

    /**
     * Volume enclosed by the SDF, building the texture on demand. 0 for a
     * point ROI.
     */
    public get volumeCm3(): number {
        if (this.isPointRoi) {
            return 0;
        }
        return this.texture?.volumeCm3 ?? 0;
    }

    /**
     * SDF-derived shape statistics (surface area, sphericity, center of mass,
     * bounding box), building the texture on demand. Undefined for a point
     * ROI.
     */
    public get statistics(): RoiStatistics | undefined {
        if (this.isPointRoi) {
            return undefined;
        }
        return this.texture?.statistics;
    }

    private _intensityStatsCached: RoiIntensityStats | null | undefined;
    private _intensityStatsCacheVersion = -1;
    private _intensityStatsCacheImage: Image3D | undefined;
    private _intensityStatsCacheGrid: Grid | undefined;

    /**
     * Mean / SD / min / max of `image`'s voxels under this ROI's mask, bundled
     * with the image for unit labelling. Undefined for a point ROI, an empty
     * mask, or a mask that hits nothing finite.
     *
     * Builds the mask on demand, as {@link statistics} does, so the figures do
     * not depend on some earlier reader having built it first, and a failed
     * build surfaces the same way for both. Cached and invalidated on mask
     * version, grid, a replaced texture or a change of sampled image, so
     * switching the referenced dataset recomputes. Only a completed read is
     * cached: caching a failure
     * would hold the empty state until an unrelated edit invalidated it.
     *
     * Withheld too while the image is still filling, and that answer is never
     * cached. A PET volume's Float32 buffer hands back zeros for slices that
     * have not arrived instead of refusing, so reducing over the mask would
     * average acquired voxels with unacquired ones and read as a measurement.
     * Nor could a cached partial result recover: slice arrival changes none of
     * the cache keys, so it would outlive the load (SR-WOR-H-37-3).
     */
    public getIntensityStatistics(
        image: Image3D
    ): RoiIntensityStats | undefined {
        const texture = this.texture;
        if (!texture || !image.isComplete) {
            return undefined;
        }
        const maskVersion = texture.signedDistanceField.dataVersion;
        if (
            this._intensityStatsCached !== undefined &&
            this._intensityStatsCacheVersion === maskVersion &&
            this._intensityStatsCacheImage === image &&
            this._intensityStatsCacheGrid === texture.textureGrid
        ) {
            return this._intensityStatsCached ?? undefined;
        }
        let computed: RoiIntensityStats | null;
        try {
            computed = this.computeIntensityStatistics(image);
        } catch (error) {
            // An absent row and a failed read look the same in the panel, so
            // the failure has to reach a log. The kind alone: the ROI and the
            // series it was drawn on are patient data.
            console.error(
                `ROI intensity statistics unavailable: ${
                    error instanceof Error ? error.name : 'unknown error'
                }`
            );
            return undefined;
        }
        this._intensityStatsCached = computed;
        this._intensityStatsCacheVersion = maskVersion;
        this._intensityStatsCacheImage = image;
        this._intensityStatsCacheGrid = texture.textureGrid;
        return computed ?? undefined;
    }

    /**
     * Reads `image` under the mask, null when no voxel under it is finite.
     *
     * @throws whatever reading the image or the mask volume throws.
     */
    private computeIntensityStatistics(
        image: Image3D
    ): RoiIntensityStats | null {
        const texture = this.texture;
        if (!texture) {
            return null;
        }
        const grid = image.grid;
        // imageIntensityPixels reads the Int16 volume directly for a
        // narrow-range image; image.floatPixelData would widen the whole
        // volume into a new Float32Array just to read it once.
        const pixels = imageIntensityPixels(image);
        const sampleImageAtWorld = (world: ReadonlyVec3): number | null => {
            // Grid.flatIndexAt is the one nearest-neighbour rule shared
            // with the cursor readout and the line-profile samplers, which
            // SR-WOR-H-17-4 requires these readouts to agree on.
            const index = grid.flatIndexAt(world);
            return index === null ? null : pixels[index];
        };
        const sdf = texture.signedDistanceField;
        const [dimX, dimY, dimZ] = sdf.size;
        const statistics = maskIntensityStatistics(
            sdf.pixels,
            dimX,
            dimY,
            dimZ,
            texture.textureGrid,
            sampleImageAtWorld
        );
        return statistics ? { statistics, image } : null;
    }

    /**
     * Method to get the BoundingBox of the structure texture.
     */
    public get boundingBox(): RotatedBoundingBox {
        if (this.isPointRoi) {
            return new RotatedBoundingBox();
        }
        return this.texture?.fullBoundingBox ?? new RotatedBoundingBox();
    }

    /**
     * Handle contour undo/redo requests from domain events
     */
    private handleContourUndoRedoRequested(
        event: ContourUndoRequestedEvent | ContourRedoRequestedEvent
    ) {
        // Gate on structure-set identity as well as ROINumber: two structure
        // sets both hold a 1-based ROI #1, so ROINumber alone would let an
        // undo restore a different structure set's same-numbered ROI.
        if (
            event.roiNumber === this.roiNumber &&
            event.structureSetUid === this.structureSetUid
        ) {
            if (!this.canModifyContours) {
                console.warn(
                    `Ignoring undo/redo for ${this.blockerAdjective}` +
                        ` structure '${this.name}'`
                );
                return;
            }
            if (this.contourTexture) {
                this.restorePixelsFromCompressed(
                    event.compressedPixels,
                    event.grid
                );
            }

            ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT);
        }
    }

    // --- Version bump utility -------------------------------------------------
    private bumpSdfVersion(): void {
        this._sdfVersion++;
        this._sdfModifiedAt = Date.now();
    }

    // --- Public getters for external dependency freshness checks --------------
    public get sdfVersion(): number {
        return this._sdfVersion;
    }

    public get sdfModifiedAt(): number {
        return this._sdfModifiedAt;
    }

    /**
     * Returns immutable revision info for dependency tracking.
     * Consumers can cache version and compare later to know if recompute needed.
     */
    public get contourRevisionInfo(): { version: number; updatedAt: number } {
        return {
            version: this._sdfVersion,
            updatedAt: this._sdfModifiedAt,
        };
    }

    private restorePixelsFromCompressed(
        compressedPixels: Uint8Array,
        grid: Grid
    ) {
        if (!this.contourTexture) {
            return;
        }

        const decompressedPixels = fflate.decompressSync(compressedPixels);
        const pixels = [];
        const sliceSize = grid.sizeX * grid.sizeY;

        for (let z = 0; z < grid.sizeZ; z++) {
            pixels.push(
                decompressedPixels.slice(z * sliceSize, (z + 1) * sliceSize)
            );
        }

        this.contourTexture.resetSdfAndGrid(pixels, grid);

        // A restore is a contour modification, and the save path only believes
        // modifications that say so: StructureSetObject.saveChanges regenerates
        // DICOM contours behind `if (roi.hasBeenModified)`, and reading
        // `roi.sequence` inside that gate clears the flag again. So an ROI
        // whose flag was cleared by an earlier flush — a save, or a switch away
        // from the structure set — would be skipped entirely by the next save
        // after an undo: the stored RTSTRUCT keeps the pre-restore contour, the
        // pending change is cleared, and the UI reports the save as complete.
        // Viewer and database disagree with nothing on screen to show it.
        //
        // markAsChanged also bumps the SDF version, so it replaces the bump
        // rather than joining it.
        this.markAsChanged();
    }

    public static toSlices(
        dicomContours: DicomContour[],
        imageGrid: Grid
    ): ContourSlice[] {
        const normalDirection = imageGrid.getUnitZ();
        const contourSlices: ContourSlice[] = [];
        for (const dicomContour of dicomContours) {
            if (dicomContour.data.length > 2) {
                // minimum 3 points to create a contour
                const contour = new Contour(dicomContour.data.map(vec3.clone));
                const contourNormalPosition = vec3.dot(
                    contour.vertices[0],
                    normalDirection
                );
                // there may be multiple contours on the same slice
                const existingSlice = contourSlices.find((slice) =>
                    ContourSlice.isSameSlice(
                        slice.normalPosition,
                        contourNormalPosition
                    )
                );
                if (!existingSlice) {
                    contourSlices.push(
                        new ContourSlice(contourNormalPosition, [contour])
                    );
                } else {
                    existingSlice.addContour(contour);
                }
            }
        }

        return contourSlices;
    }

    /**
     * Re-reads this ROI's contours from its domain {@link DicomRoi} and
     * rebuilds the engine-side {@link ContourSequence}, discarding any
     * cached SDF texture so the next texture access re-rasterises from
     * the new sequence.
     *
     * Why this exists: the constructor snapshots `parentRoi.contours`
     * ONCE, and no event path refreshes it afterwards — the changeset
     * 'modified' sync only covers rendering properties (name, colour,
     * approval). Any flow that creates an ROI first (the 'created'
     * flush constructs this object) and writes explicit DICOM contours
     * afterwards would otherwise render — and report volume/emptiness
     * for — the stale empty snapshot forever, while the exported DICOM
     * carries the real geometry the user cannot see or verify.
     *
     * NOT guaranteed: this is an explicit, caller-invoked resync, not a
     * subscription — later `DicomRoi.contours` writes need their own
     * call — and it does not publish a view-update event, so the caller
     * must trigger a redraw for the change to appear on screen.
     */
    public reloadContoursFromDicom(): void {
        const slices = RegionOfInterest.toSlices(
            this.parentRoi.contours,
            this._imageGrid
        );
        this.contourSequence.replaceSlices(slices);
        // The cached texture (possibly already rasterised from the stale
        // sequence during an earlier render) no longer matches; free its
        // GPU memory now — dropping the reference alone would leak the
        // WebGL texture — and let the texture getter rebuild lazily.
        this.contourTexture?.delete();
        this.contourTexture = undefined;
        this._renderErrors = '';
        // The sequence IS the source of truth after this call: leaving
        // `_allSlicesChanged` false keeps `hasBeenModified` false, so
        // StructureSetObject.saveChanges will NOT round-trip the exact
        // DICOM points through the SDF texture on save.
        this._allSlicesChanged = false;
        this.bumpSdfVersion();
    }

    public startModify(
        operation: string,
        options?: StartModifyOptions
    ): TextureWrite {
        this._ensureCanModifyContours(operation);
        if (this.isPointRoi) {
            throw new Error(
                `Cannot modify point ROI '${this.name}' ` +
                    'through SDF pipeline'
            );
        }

        this._modificationStarted = true;

        let initialPixels: Uint8Array[] | undefined;
        let initialGrid: Grid | undefined;

        const texture = this.texture;
        // The pre-operation state seeds the undo history's first entry.
        // An authoring write that lands on a structure with no contours
        // has no such state: the structure holds nothing to return to,
        // and recording "no contours" put an empty structure in the
        // history as the earliest reachable state — so the first undo of
        // a generated structure discarded the whole thing. An incremental
        // edit (a brush stroke) is NOT exempt: undoing a first stroke back
        // to an empty structure is exactly what the user means.
        const authorsFromNothing =
            options?.authorsWholeStructure === true &&
            texture?.isEmpty === true;

        if (texture && !this._touched && !authorsFromNothing) {
            initialPixels = texture.pixels?.map((slice) => slice.slice());
            initialGrid = texture.textureGrid;
        }

        return new TextureWrite(
            this,
            operation,
            initialPixels,
            initialGrid,
            options?.restoredOnFailure
        );
    }

    /**
     * Replaces the ROI texture and records the change for undo/redo.
     * Combines startModify, texture assignment, and finish into
     * a single call for operations that produce a new texture.
     *
     * Every caller authors the structure's whole content (margin, ring,
     * union, threshold extraction, the SDF post-processing operations),
     * so the write declares itself as such — see
     * {@link StartModifyOptions.authorsWholeStructure}.
     */
    public replaceTexture(texture: ContourTexture, operation: string): void {
        const modify = this.startModify(operation, {
            authorsWholeStructure: true,
        });
        this.texture = texture;
        modify.finish();
    }

    public markAsChanged(): void {
        this._allSlicesChanged = true;
        this._modificationStarted = false;
        this._touched = true;
        this.bumpSdfVersion(); // bump on any modification completion
    }

    /**
     * Reset the modification flag without marking the ROI
     * as changed. Used when a modification is cancelled and
     * the original data has been restored.
     */
    public resetModificationFlag(): void {
        this._modificationStarted = false;
    }

    public get name(): string {
        return this._roiName;
    }

    /**
     * Handle ROI property updates from domain events
     * Updates engine state and triggers viewport updates based on event data
     */
    private handleRoiPropertiesUpdated(event: RoiPropertiesUpdatedEvent): void {
        // Gate on structure-set identity as well as ROINumber: two structure
        // sets both hold a 1-based ROI #1, so ROINumber alone would apply a
        // different structure set's rename/recolor to this ROI.
        if (
            event.roiNumber !== this.roiNumber ||
            event.structureSetUid !== this.structureSetUid
        ) {
            return;
        }

        const properties = event.updatedProperties;

        if (properties.visible !== undefined) {
            this._visible = properties.visible;
        }

        if (properties.color !== undefined) {
            this.colorRgb8 = properties.color;
        }

        if (properties.contourThickness !== undefined) {
            this._contourThickness = properties.contourThickness;
        }

        if (properties.name !== undefined) {
            this._roiName = properties.name;
        }

        ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT, this.boundingBox);
    }

    /**
     * Method to delete the {@link RegionOfInterest}.\
     * This de-allocates all the WebGL memory reserved by the different data models within this {@link RegionOfInterest}.
     */
    public delete() {
        // `builtTexture`, not the `texture` getter: the getter rasterises an
        // SDF for an ROI that has never been drawn, so reading it here would
        // allocate a texture on the GPU purely to free it on the next line.
        this.builtTexture?.delete();
        this._eventSubscriptions?.unsubscribeAll();
        this._deleted = true;
    }
}
